import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, gql, useLazyQuery } from '@apollo/client';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import SalesOrderUnserializedPickScan from './SalesOrderUnserializedPickScan';
import { useRootStore } from '@/lib/providers/RootProvider';

const CLIENT_ORDER_AND_ZONES_WITH_PRODUCT = gql`
  query salesOrder(
    $id: ID!
    $vendorId: ID!
    $vendorStatuses: [String!]
    $fdaProductId: ID
    $upcProductId: ID
  ) {
    salesOrder(id: $id, vendorId: $vendorId, vendorStatuses: $vendorStatuses) {
      id
      vendorSalesOrderNumber
      vendorStatus
      clientStatus
      discountAmount
      shippingAmount
      taxAmount
      convenienceFeeAmount
      subtotalAmount
      totalAmount
      createdAt
      updatedAt
      isReturnOrder
      originalOrderId
      orderItems {
        id
        quantity
        isShortDated
        # The line's second pick rule. The Lot/Exp dropdown on this screen is already narrowed
        # server-side to chargeback-eligible lots, which makes the restriction INVISIBLE — a
        # picker just sees fewer lots than the shelf holds. The badge is what turns that into
        # something explicable instead of a suspected bug.
        isChargeback
        serializedItemOfStatusCount(status: "PICKED")
        unserializedItemOfStatusCount(status: "PICKED")
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
          upcProduct {
            id
            upc
          }
        }
      }
      client {
        id
        companyName
      }
      isDestructionOrder
    }
    zonesWithProduct(
      companyId: $vendorId
      fdaProductId: $fdaProductId
      upcProductId: $upcProductId
      salesOrderId: $id
    ) {
      id
      name
      zoneType
      shortDatedQuantity
      pickSequence
    }
    unserializedItemStates(
      salesOrderId: $id
      companyId: $vendorId
      fdaProductId: $fdaProductId
      upcProductId: $upcProductId
      status: "PICKED"
    ) {
      id
      quantity
      lot
      expiration
      zoneId
      zone {
        id
        name
      }
    }
  }
`;

const CLIENT_PRODUCT_ZONE_INVENTORY_BALANCES = gql`
  query paginatedZoneInventoryBalancesByZone(
    $zoneId: ID!
    $productId: ID!
    $companyId: ID!
    $balanceBucket: String
    $salesOrderId: ID
  ) {
    paginatedZoneInventoryBalancesByZone(
      zoneId: $zoneId
      productId: $productId
      companyId: $companyId
      balanceBucket: $balanceBucket
      salesOrderId: $salesOrderId
      paginationAttributes: { limit: 10, skip: 0 }
    ) {
      zoneInventoryBalances {
        id
        stocked
        quarantined
        destroyed
        lot
        expiration
        zone {
          id
          name
        }
      }
      pagination {
        totalRecords
      }
    }
  }
`;

const UNSERIALIZED_PICK = gql`
  mutation pickUnserializedSalesOrder(
    $salesOrderId: ID!
    $zoneId: ID!
    $zoneInventoryBalanceId: ID!
    $quantity: Int!
    $companyId: ID!
  ) {
    pickUnserializedSalesOrder(
      salesOrderId: $salesOrderId
      zoneId: $zoneId
      zoneInventoryBalanceId: $zoneInventoryBalanceId
      quantity: $quantity
      companyId: $companyId
    ) {
      id
    }
  }
`;

const UNSERIALIZED_STOCK = gql`
  mutation stockUnserializedSalesOrder(
    $salesOrderId: ID!
    $zoneId: ID!
    $unserializedItemStateId: ID!
    $quantity: Int!
    $companyId: ID!
  ) {
    stockUnserializedSalesOrder(
      salesOrderId: $salesOrderId
      zoneId: $zoneId
      unserializedItemStateId: $unserializedItemStateId
      quantity: $quantity
      companyId: $companyId
    ) {
      id
    }
  }
`;

function SalesOrderUnserializedPickScanData(props) {
  const { id } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const [pickUnserializedSalesOrder] = useMutation(UNSERIALIZED_PICK);
  const [stockUnserializedSalesOrder] = useMutation(UNSERIALIZED_STOCK);
  const [searchParams] = useSearchParams();
  let fdaProductId = searchParams.get('fdaProductId');
  let upcProductId = searchParams.get('upcProductId');
  let isShortDatedParam = searchParams.get('isShortDated');
  // The zone the picking screen's walk sent the picker to, if any.
  const suggestedZoneId = searchParams.get('zoneId') || null;
  if (fdaProductId === 'undefined') fdaProductId = undefined;
  if (upcProductId === 'undefined') upcProductId = undefined;
  const isShortDated = isShortDatedParam === 'true';

  const { data, error, loading, refetch } = useQuery(
    CLIENT_ORDER_AND_ZONES_WITH_PRODUCT,
    {
      fetchPolicy: 'network-only',
      variables: { id, vendorId, vendorStatuses: ['picking'], fdaProductId, upcProductId }
    }
  );

  const [
    getClientProductZoneInventoryBalances,
    {
      data: clientProductZoneInventoryBalancesData,
      loading: zoneInventoryBalancesLoading,
      error: zoneInventoryBalancesError
    }
  ] = useLazyQuery(CLIENT_PRODUCT_ZONE_INVENTORY_BALANCES, {
    fetchPolicy: 'network-only'
  });

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;

  const { salesOrder, zonesWithProduct, unserializedItemStates } = data || {};
  const { paginatedZoneInventoryBalancesByZone } =
    clientProductZoneInventoryBalancesData || {};
  const { zoneInventoryBalances } = paginatedZoneInventoryBalancesByZone || {};

  return (
    <SalesOrderUnserializedPickScan
      salesOrder={salesOrder}
      companyId={vendorId}
      refetch={refetch}
      fdaProductId={fdaProductId}
      upcProductId={upcProductId}
      zonesWithProduct={zonesWithProduct}
      getClientProductZoneInventoryBalances={getClientProductZoneInventoryBalances}
      zoneInventoryBalances={zoneInventoryBalances}
      zoneInventoryBalancesLoading={zoneInventoryBalancesLoading}
      zoneInventoryBalancesError={zoneInventoryBalancesError}
      pickUnserializedSalesOrder={pickUnserializedSalesOrder}
      stockUnserializedSalesOrder={stockUnserializedSalesOrder}
      unserializedItemStates={unserializedItemStates}
      isShortDatedParam={isShortDated}
      suggestedZoneId={suggestedZoneId}
      {...props}
    />
  );
}

export default SalesOrderUnserializedPickScanData;
